export const TRANSLATION_TEXTAREA_DATA_ATTRIBUTE = "data-translation-textarea";
export const TRANSLATION_TEXTAREA_DATA_KEY = "translationTextarea";
export const TRANSLATION_VIEW_SEGMENT_TABLE_CONTAINER_ID =
  "translation-view-segment-table-container";

/**
 * Focuses on the next or previous textarea element based on the arrow key pressed.
 * This has no react related dependencies so it can be used in any environment.
 * @param event - The keyboard event object.
 */
export const segmentTextsFocusHandler = (event: KeyboardEvent): void => {
  const focusFirstElement = (): void => {
    const firstElement = document.querySelector<HTMLTextAreaElement>(
      `[${TRANSLATION_TEXTAREA_DATA_ATTRIBUTE}="0"]`
    );
    if (firstElement) {
      firstElement.focus();
      const container = document.getElementById(
        TRANSLATION_VIEW_SEGMENT_TABLE_CONTAINER_ID
      );
      container?.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  const focusElement = (index: number): boolean => {
    const element = document.querySelector<HTMLTextAreaElement>(
      `[${TRANSLATION_TEXTAREA_DATA_ATTRIBUTE}="${index}"]`
    );
    if (element) {
      element.focus();
      if (index !== 0) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        const container = document.getElementById(
          TRANSLATION_VIEW_SEGMENT_TABLE_CONTAINER_ID
        );
        container?.scrollTo({ top: 0, behavior: "smooth" });
      }
      return true;
    }
    return false;
  };
  if (event.key === "ArrowDown") {
    event.preventDefault();
    const dataset = (event.target as HTMLElement).dataset;
    if (dataset?.[TRANSLATION_TEXTAREA_DATA_KEY]) {
      let index = Number(dataset[TRANSLATION_TEXTAREA_DATA_KEY]) + 1;
      const stop = 40;
      while (!focusElement(index)) {
        index++;
        if (index >= stop) {
          break;
        }
      }
    } else {
      focusFirstElement();
    }
  }
  if (event.key === "ArrowUp") {
    event.preventDefault();
    const dataset = (event.target as HTMLElement).dataset;
    if (dataset?.[TRANSLATION_TEXTAREA_DATA_KEY]) {
      let index = Number(dataset[TRANSLATION_TEXTAREA_DATA_KEY]) - 1;
      const stop = 0;
      while (!focusElement(index)) {
        index--;
        if (index <= stop) {
          break;
        }
      }
    } else {
      focusFirstElement();
    }
  }
};

export const focusSegment = (element: HTMLTextAreaElement | null): void => {
  if (element) {
    element.focus();
  }
};

const getFocusableSegmentAtIndex = (
  index: number
): HTMLTextAreaElement | null => {
  const element = document.querySelector<HTMLTextAreaElement>(
    `[${TRANSLATION_TEXTAREA_DATA_ATTRIBUTE}="${index}"]`
  );
  return element || null;
};

export const remainFocusOnSegment = (index: number): void => {
  const element = getFocusableSegmentAtIndex(index);
  if (element) {
    element.focus();
  }
};
export const getPreviousFocusableSegment = (
  index: number
): HTMLTextAreaElement | null => {
  let previousIndex = index - 1;
  let element: HTMLTextAreaElement | null = null;
  const stop = 0;
  while (!element && previousIndex >= stop) {
    element = getFocusableSegmentAtIndex(previousIndex);
    previousIndex--;
  }
  return element;
};

export const getNextFocusableSegment = (
  index: number
): HTMLTextAreaElement | null => {
  let nextIndex = index + 1;
  let element: HTMLTextAreaElement | null = null;
  const stop = index + 41;

  while (!element && nextIndex < stop) {
    element = getFocusableSegmentAtIndex(nextIndex);
    nextIndex++;
  }
  return element;
};

export const changeWordCountInput = (
  value: number | undefined,
  op: "add" | "subtract" = "subtract"
): void => {
  if (!value) {
    return;
  }
  const WORD_COUNT_INPUT_DATA_ATTRIBUTE = "txu-word-count-input";
  const element = document.querySelector<HTMLInputElement>(
    `[${WORD_COUNT_INPUT_DATA_ATTRIBUTE}]`
  );
  if (!element) {
    return;
  }
  let currentValue = Number(element.value);
  if (isNaN(currentValue)) {
    return;
  }
  if (op === "add") {
    currentValue += value;
  } else {
    currentValue -= value;
  }
  const zeroCurrent = Math.max(currentValue, 0);
  element.value = zeroCurrent.toString();
};
